/**
 * Travel Requirements Service
 * 
 * Fetches visa requirements, health requirements, and entry restrictions
 * Uses TravelBuddy API via Cloud Function with fallback to existing visa checker
 */

import { httpsCallableWithTimeout, functions } from '@/lib/functions';
import { checkVisaRequirements, type VisaRequirement } from './visaChecker';
import type { Trip } from './trips';
import { fromIso2CountryCode, toIso2CountryCode } from '@/utils/countryIso2';
import {
  getTripDestinationCountryLabel,
  resolveTripDestinationCountryIso2,
} from '@/utils/tripDestinationCountry';
import { isInternalDebugBuild } from './logger';

export type HealthRequirement = {
  type: 'vaccination' | 'health_certificate' | 'medical_clearance' | 'other';
  name: string;
  description?: string;
  required: boolean;
  dueDate?: Date;
};

export type TravelRequirements = {
  visaRequired: boolean;
  visaStatus?: 'visa_free' | 'visa_required' | 'visa_on_arrival' | 'e_visa' | 'unknown';
  visaNotes?: string;
  stayDuration?: number;
  eVisaLink?: string;
  embassyLink?: string;
  healthRequirements: HealthRequirement[];
  entryRestrictions?: string[];
  lastUpdated?: string;
  source: 'travelbuddy' | 'visa_checker' | 'fallback';
  /**
   * Debug-only metadata. This should never be shown to end users in production.
   * Only populated in dev builds.
   */
  debug?: {
    failureReason?: 'missing_api_key' | 'unmapped_country' | 'provider_error';
    cacheHit?: boolean;
    rawSource?: string;
    callableErrorCode?: string;
    callableErrorMessage?: string;
    providerHttpStatus?: number;
    providerMessage?: string;
  };
};

type GetTravelRequirementsRequest = {
  passportCountry: string;
  destinationCountry: string;
  tripStartDate: string;
  tripEndDate: string;
};

type GetTravelRequirementsResponse = {
  visaRequired: boolean;
  visaStatus?: string;
  visaNotes?: string;
  stayDuration?: number;
  eVisaLink?: string;
  embassyLink?: string;
  healthRequirements?: Array<{
    type: string;
    name: string;
    description?: string;
    required: boolean;
  }>;
  entryRestrictions?: string[];
  lastUpdated?: string;
  source: string;
  failureReason?: 'missing_api_key' | 'unmapped_country' | 'provider_error';
  cacheHit?: boolean;
  providerHttpStatus?: number;
  providerMessage?: string;
};

const getTravelRequirementsCallable = httpsCallableWithTimeout<
  GetTravelRequirementsRequest,
  GetTravelRequirementsResponse
>(functions, 'getTravelRequirements', 45000);

type TravelRequirementsCacheEntry = {
  savedAt: number;
  value: TravelRequirements;
};

/** Visa/entry rules change infrequently; 14 days aligns with backend Firestore cache. */
const TRAVEL_REQUIREMENTS_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const travelRequirementsCache = new Map<string, TravelRequirementsCacheEntry>();
const travelRequirementsInFlight = new Map<string, Promise<TravelRequirements | null>>();

const toIsoDateString = (value: any): string => {
  try {
    const d = value instanceof Date ? value : new Date(value);
    return d.toISOString();
  } catch {
    return new Date().toISOString();
  }
};

/**
 * Get travel requirements for a trip
 * Uses TravelBuddy API via Cloud Function, falls back to visa checker if unavailable
 */
export async function getTravelRequirements(
  trip: Trip,
  passportCountry: string
): Promise<TravelRequirements | null> {
  try {
    const isStagingDebug = isInternalDebugBuild();
    let callableError: { code?: string; message?: string } | null = null;

    const destination = trip.destination || trip.destinations?.[0]?.city;
    if (!destination || !passportCountry) {
      return null;
    }

    // Resolve ISO2 passport + destination codes (provider expects ISO2).
    const normalizeIso2 = (value: string) => {
      const v = (value || '').trim();
      if (!v) return v;
      const iso2 = toIso2CountryCode(v);
      return iso2 || (v.length === 2 ? v.toUpperCase() : v);
    };

    const passportNorm = normalizeIso2(passportCountry);

    const destinationIso2 = resolveTripDestinationCountryIso2(trip);

    if (!destinationIso2) {
      // Destination is not structured enough to perform reliable checks.
      return null;
    }

    const tripStartIso = toIsoDateString(trip.startDate);
    const tripEndIso = toIsoDateString(trip.endDate);
    const tripDateBucket = `${tripStartIso.slice(0, 10)}:${tripEndIso.slice(0, 10)}`;
    const cacheKey = `${passportNorm}:${destinationIso2}:${tripDateBucket}`.toUpperCase();

    const cached = travelRequirementsCache.get(cacheKey);
    if (cached && Date.now() - cached.savedAt < TRAVEL_REQUIREMENTS_CACHE_TTL_MS) {
      return cached.value;
    }

    const inFlight = travelRequirementsInFlight.get(cacheKey);
    if (inFlight) {
      return await inFlight;
    }

    // Same-country travel is visa-free (avoid calling backend and prevent "US->US requires visa" confusion).
    if (passportNorm && destinationIso2 && passportNorm === destinationIso2) {
      const value: TravelRequirements = {
        visaRequired: false,
        visaStatus: 'visa_free',
        visaNotes: `No visa required for citizens of ${passportNorm} traveling within ${destinationIso2}.`,
        healthRequirements: [],
        source: 'fallback',
        lastUpdated: new Date().toISOString(),
      };
      travelRequirementsCache.set(cacheKey, { savedAt: Date.now(), value });
      return value;
    }

    const promise = (async (): Promise<TravelRequirements | null> => {
      // Try to get requirements from Cloud Function (which uses TravelBuddy API)
      try {
        const result = await getTravelRequirementsCallable({
          passportCountry: passportNorm,
          destinationCountry: destinationIso2,
          tripStartDate: tripStartIso,
          tripEndDate: tripEndIso,
        });

        if (result.data) {
          const mapped: TravelRequirements = {
            visaRequired: result.data.visaRequired || false,
            visaStatus: result.data.visaStatus as TravelRequirements['visaStatus'],
            visaNotes: result.data.visaNotes,
            stayDuration: result.data.stayDuration,
            eVisaLink: result.data.eVisaLink,
            embassyLink: result.data.embassyLink,
            healthRequirements: (result.data.healthRequirements || []).map((req) => ({
              type: req.type as HealthRequirement['type'],
              name: req.name,
              description: req.description,
              required: req.required,
            })),
            entryRestrictions: result.data.entryRestrictions,
            lastUpdated: result.data.lastUpdated,
            source: result.data.source as 'travelbuddy' | 'visa_checker' | 'fallback',
          };

          if (isStagingDebug) {
            mapped.debug = {
              failureReason: result.data.failureReason,
              cacheHit: result.data.cacheHit,
              rawSource: result.data.source,
              providerHttpStatus: result.data.providerHttpStatus,
              providerMessage: result.data.providerMessage,
            };
          }

          travelRequirementsCache.set(cacheKey, { savedAt: Date.now(), value: mapped });
          return mapped;
        }
      } catch (error: any) {
        callableError = {
          code: typeof error?.code === 'string' ? error.code : undefined,
          message: typeof error?.message === 'string' ? error.message : undefined,
        };
        console.warn('[TravelRequirements] Cloud Function call failed, falling back to visa checker:', error);
      }

      // Fallback to existing visa checker (uses same ISO2 destination resolution).
      const visaRequirement = await checkVisaRequirements(trip, passportCountry);
      if (visaRequirement) {
        const mapped: TravelRequirements = {
          visaRequired: visaRequirement.visaRequired,
          visaStatus: visaRequirement.status,
          visaNotes: visaRequirement.notes,
          healthRequirements: [],
          source: 'visa_checker',
          lastUpdated: visaRequirement.lastUpdated,
        };
        if (isStagingDebug && callableError) {
          mapped.debug = {
            failureReason: 'provider_error',
            rawSource: 'callable_error',
            callableErrorCode: callableError.code,
            callableErrorMessage: callableError.message,
          };
        }
        travelRequirementsCache.set(cacheKey, { savedAt: Date.now(), value: mapped });
        return mapped;
      }

      if (isStagingDebug && callableError) {
        const fallback: TravelRequirements = {
          visaRequired: true,
          visaStatus: 'unknown',
          visaNotes: 'Unable to verify visa requirements. Please check with the destination country.',
          healthRequirements: [],
          source: 'fallback',
          lastUpdated: new Date().toISOString(),
          debug: {
            failureReason: 'provider_error',
            rawSource: 'callable_error',
            callableErrorCode: callableError.code,
            callableErrorMessage: callableError.message,
          },
        };
        travelRequirementsCache.set(cacheKey, { savedAt: Date.now(), value: fallback });
        return fallback;
      }

      return null;
    })();

    travelRequirementsInFlight.set(cacheKey, promise);
    try {
      return await promise;
    } finally {
      if (travelRequirementsInFlight.get(cacheKey) === promise) {
        travelRequirementsInFlight.delete(cacheKey);
      }
    }
  } catch (error) {
    console.error('[TravelRequirements] Error getting travel requirements:', error);
    return null;
  }
}

/**
 * Extract country name from location string
 * @deprecated Prefer resolveTripDestinationCountryIso2 / getTripDestinationCountryLabel
 */
function extractCountry(location: string): string {
  // Handle "City, Country" format
  const parts = location.split(',').map((p) => p.trim());
  if (parts.length > 1) {
    return parts[parts.length - 1];
  }
  return location;
}

